// Timestamp Conversion Utilities
export const convertFirestoreTimestamps = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;

  // Handle Firestore Timestamp objects (both from SDK and serialized)
  if (typeof obj === 'object' && obj.seconds !== undefined && obj.nanoseconds !== undefined) {
    try {
      // If it has a toDate method (SDK object)
      if (typeof obj.toDate === 'function') {
        return obj.toDate().toISOString();
      }
      // If it's a plain object with seconds (serialized)
      return new Date(obj.seconds * 1000 + obj.nanoseconds / 1000000).toISOString();
    } catch {
      return new Date().toISOString();
    }
  }

  if (Array.isArray(obj)) return obj.map(convertFirestoreTimestamps);

  if (typeof obj === 'object') {
    const converted: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        converted[key] = convertFirestoreTimestamps(obj[key]);
      }
    }
    return converted;
  }

  return obj;
};
export const processProjectData = (projectData: any): any => convertFirestoreTimestamps(projectData);

// Audit Entry Interface and Function
export interface AuditEntry {
  id: string;
  timestamp: string;
  userId: string;
  userName: string;
  action: string;
  details: string;
  itemId?: number;
  oldValue?: any;
  newValue?: any;
}

export const createAuditEntry = (userId: string, userName: string, action: string, details: string, itemId?: number, oldValue?: any, newValue?: any): AuditEntry => ({
  id: `audit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, timestamp: new Date().toISOString(), userId, userName, action, details, itemId, oldValue, newValue
});

// General Utility Functions
export const isDeadlineApproaching = (deadline?: string, days: number = 3): boolean => {
  if (!deadline) return false;
  const deadlineDate = new Date(deadline); const now = new Date();
  const diffTime = deadlineDate.getTime() - now.getTime(); const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= days && diffDays >= 0;
};
export const isOverdue = (deadline?: string): boolean => {
  if (!deadline) return false;
  const deadlineDate = new Date(deadline); const now = new Date(); return deadlineDate < now;
};
export const downloadFile = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob); const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
};
export const removeUndefinedValues = (obj: any): any => {
  if (obj === null || obj === undefined) return null;
  if (Array.isArray(obj)) return obj.map(item => removeUndefinedValues(item)).filter(item => item !== undefined);
  if (typeof obj === 'object') { const cleaned: any = {}; for (const key in obj) { if (obj.hasOwnProperty(key) && obj[key] !== undefined) { cleaned[key] = removeUndefinedValues(obj[key]); } } return cleaned; }
  return obj;
};

// Collision-resistant numeric ID generator for items / fund entries.
// The previous pattern (Date.now() + random 0-999) could produce the same ID
// for two entities created near-simultaneously; because Firestore documents
// are keyed by this ID, a collision silently overwrites an existing record.
// This spreads IDs by a factor of 1000 and guarantees strictly increasing
// values within a session. (A future refactor should move to string UUIDs.)
let lastGeneratedId = 0;
export const generateItemId = (): number => {
  // Date.now()*1000 stays well below Number.MAX_SAFE_INTEGER (~9e15)
  let id = Date.now() * 1000 + Math.floor(Math.random() * 1000);
  if (id <= lastGeneratedId) id = lastGeneratedId + 1;
  lastGeneratedId = id;
  return id;
};

// Realtime-sync coordination shared by App (per-project item listeners) and
// ProjectView (item modals). While a modal is open, listener updates are
// cached here instead of applied; the last modal to close calls onResume.
export const itemsSync = {
  listeners: new Map<string, () => void>(),
  items: new Map<string, any[]>(),
  modalOpenCount: 0,
  pendingUpdate: false,
  onResume: null as null | (() => void),
};
